import React, { useState, useEffect } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { chatbotWindowStyles } from './chatbotWindowStyles.js';

interface ChatbotWindowProps {
  isOpen: boolean;
  onClose: () => void;
  chatBotUrl: string;
}

export const ChatbotWindow: React.FC<ChatbotWindowProps> = ({
  isOpen,
  onClose,
  chatBotUrl,
}) => {
  const styles: Record<string, string> = chatbotWindowStyles();
  const [isLoading, setIsLoading] = useState(true);

  // Reset loading state when chatbot is opened
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);

      // Listen for messages from the iframe
      const handleMessage = (event: MessageEvent) => {
        // Check if message is from the chatbot iframe and contains ready message
        if (
          event.origin ===
            new URL(chatBotUrl || 'https://auth.upboundleap.click/services/va/')
              .origin &&
          (event.data === 'chatbot-ready' ||
            event.data.type === 'chatbot-ready')
        ) {
          setIsLoading(false);
        }
      };

      window.addEventListener('message', handleMessage);

      return () => {
        window.removeEventListener('message', handleMessage);
      };
    }
  }, [isOpen, chatBotUrl]);

  // Show iframe after initial load
  const handleIframeLoad = () => {
    setTimeout(() => setIsLoading(false), 500);
  };

  if (!isOpen) return null;

  return (
    <OutsideClickHandler onOutsideClick={onClose}>
      <div className={styles.chatbotWindow}>
        {/* Close button */}
        <div
          className={styles.closeBtn}
          onClick={onClose}
          role="button"
          aria-label="Close chat"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onClose();
            }
          }}
        >
          <div className={styles.closeBtnIcon}>
            <div className={styles.closeBtnLine1} />
            <div className={styles.closeBtnLine2} />
          </div>
        </div>

        {/* Loading spinner */}
        {isLoading && (
          <div className={styles.loaderContainer}>
            <div className={styles.spinner} />
            <p className={styles.loadingText}>Loading Chatbot...</p>
          </div>
        )}

        {/* Chatbot iframe */}
        <iframe
          src={chatBotUrl || 'https://auth.upboundleap.click/services/va/'}
          title="Upbound Leap Chatbot"
          className={styles.chatbotIframe}
          onLoad={handleIframeLoad}
          style={{ opacity: isLoading ? 0 : 1 }}
        />
      </div>
    </OutsideClickHandler>
  );
};
